import dompurify from 'dompurify';
import { AuthProvider } from '../web/models/authProvider';
import { appContext } from '../web/models/getHelp/appContext';
import { DiagnosticCapabilityType } from '../web/models/getHelp/diagnosticCapabilities';
import { GetHelpBaseUrl } from '../web/models/getHelp/environmentConfig';
import { getRandomGuidString, isArticleSupportedInApp } from '../web/utils/getHelputils';
import { captureContentUpdate } from '../web/utils/oneDsUtil';
import { getCookie, getMetaTagAttributeValue, getQueryParamDictionary, getSocMarket, getSourceAppFromUrl, isGetHelpSourceApp, isValidHTML } from '../web/utils/statelessUtil';
import { urlParse } from '../web/utils/urlUtils';
import { AgeBracket, AlchemyApp, AlchemySolutionsCall, AlchemySolutionsCallType, AlchemySolutionsPayload, ContactFormBrand, AutoSuggestResult, UserInfo } from './models/apiModels';
import { AutoSuggestConstants, AwaMetaTagType, Environment, SourceApps, URLs, LinkForLoggingAttribute, M365UpsellNonAgentModalityId, AutoSuggestV2Constants, Namespaces, ContentAttribute, SMCExperimentQueryMaxCharacter, QueryMaxCharacter, isCMCRequestCreationDeeplinkKey } from './models/constants/constants';
import { IAppState, TenantInfo } from './models/reduxState';
import { LoggingHtmlAttribute } from './packages/contactUs/models/contactUsModels';
import { ModalityResponseModel, WaitTimeResponseModel, WaitTimeResponseReason, NonAgentModality } from './packages/contactUs/models/modalityModels';
import { AttachmentsConstants, CustomerContact, FreelanceRequest, MetaDataKeys, RaveMetaData } from './packages/contactUs/models/requestModels';
import { QueryType } from '../web/models/queryType';

export const MicrosoftEmailRegex: RegExp = /^[^@]+@(?:[^.]+\.)*microsoft\.com$/;
export const StudentEmailRegex: RegExp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.+-]+\.edu$/;
export const AlchemyInsightHTMLAllowedRegExp: RegExp = /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|ms-[a-z0-9-]+):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i;

// Enums used for userAgent
export enum PlatformVersion {
  Windows10 = 'Windows 10',
  Windows11 = 'Windows 11',
  Unknown = 'Unknown'
}

export enum BrowserOffset {
  Edg = 'Edg/', // Edge (Chromium)
  MSIE = 'MSIE/', // MSIE
  MSIE11 = 'Trident/', // MSIE 11+
  Chrome = 'Chrome/',
  Firefox = 'Firefox/'
}

export enum Browser {
  Edge = 'Microsoft Edge',
  InternetExplorer = 'Microsoft Internet Explorer',
  Chrome = 'Chrome',
  Firefox = 'Firefox',
  Unknown = 'Unknown'
}

export const FormUrlEncodedHeaders = {
  'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
};

export const JsonHeaders = {
  'Content-Type': 'application/json; charset=UTF-8',
};

// Supported Mac namespaces.
export const macNamespaces = ['macexcel', 'macpowerpoint', 'macword'];

export const win32Namespaces = [
  'excel',
  'winword',
  'powerpnt',
  'outlook',
  'onenote',
];

// Supported UWP namespaces.
export const uwpNamespaces = ['onenoteim'];

export enum Theme {
  legacy = -1,
  colorful = 0,
  darkgray = 3,
  black = 4,
  white = 5,
  fluent = 6,
  highcontrastblack = 7,
  fluentcolorful = 10,
  fluentdarkgray = 13,
  fluentblack = 14,
  fluentwhite = 15
}

export const mapTheme = (officejsTheme: string): string => {
  switch (officejsTheme) {
    // mappings from OfficeJS Office.officeTheme.backgroundColor value to SOC css theme values
    case '#e6e6e6':
      return 'colorful';
    case '#666666':
      return 'darkgray';
    case '#262626':
    case '#1B1A19':
      return 'black';
    case '#ffffff':
      return 'white';
    default:
      return 'white'; // cases handled match current potential OfficeJS Office.officeTheme.backgroundColor, if the default is hit then the OfficeJS api has changed and we default to a white theme
  }
};

export const handleTabOnSearchIcon = (e: any) => {
  if (e.key === 'Tab' && !e.shiftKey) {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    // Check if user is using firefox as firefox does not allow programatically focusing on an iframe element and instead we must focus on an element within the iframe
    if (navigator.userAgent.toLowerCase().indexOf('firefox') !== -1) {
      const aElementArray = (document.getElementById(
        'ocSearchIFrame'
      ) as HTMLIFrameElement).contentDocument.getElementsByTagName('a');
      if (aElementArray.length > 1) {
        aElementArray[0].focus();
      }
    } else {
      document.getElementById('ocSearchIFrame').focus();
    }
  }
};

// Function to grab the value for a given key in the userContext object stored as a session cookie
export const getFromUserContextCookie = (key: string) => {
  const userContextJsonString = getCookie('userContext', document.cookie);
  const userContextObj = JSON.parse(userContextJsonString);
  if (userContextObj === null) {
    return null;
  }
  return userContextObj[key];
};

export const getDisplayNamespace = (namespace: string) => {
  if (!namespace) {
    return '';
  }
  switch (namespace.toLowerCase()) {
    case 'winword':
    case 'wdwaenduser':
    case 'macword':
    case 'iphoneword':
    case 'wd_android_phone':
      return 'winword';
    case 'excel':
    case 'xlwaenduser':
    case 'macexcel':
    case 'iphoneexcel':
    case 'xl_android_phone':
      return 'excel';
    case 'powerpnt':
    case 'ppwaenduser':
    case 'macpowerpoint':
    case 'iphoneppt':
    case 'ppt_android_phone':
      return 'powerpnt';
    case 'onenote':
    case 'onwaenduser':
    case 'maconenote':
    case 'mob_on_iphone':
    case 'mob_on_and':
      return 'onenote';
    case 'visio365':
    case 'viwaenduser':
    case 'visio_std':
    case 'visioiphone':
    case 'visioipad':
    case 'visio':
      return 'visio';
    default:
      return '';
  }
};

export const enum ClientAuthProvider {
  AAD = 'ADAL',
  MSA = 'LiveId',
}

export const enum ClientPromptForLoginState {
  FAILED_OR_CANCELLED,
  SUCCESS,
}

export const getAuthProviderFromReferrer = () => {
  try {
    const hostname = urlParse(Office.context.document.url).hostname;
    return hostname.indexOf('live') !== -1 &&
      hostname.indexOf('sharepoint') === -1
      ? AuthProvider.WindowsLiveId
      : AuthProvider.AAD;
  } catch (error) {
    const logHandlerTags = {
      content: {
        contentId: 'getAuthProviderError',
        contentName: 'getAuthProviderError',
        formnm: 'maxFunnel',
        areaName: 'backgroundAuth',
        url: Office.context.document.url,
        href: window.location.href,
        errorMessage: error.message,
      },
      behavior: 'OTHER',
    };
    captureContentUpdate(logHandlerTags);
  }
};

// Function to check if deep link is enabled and return valid deep link query, otherwise return original contactSearchedQuery
export const getValidDeepLink = (isDeepLinkEnabled: boolean, contactSearchedQuery: string) => {
  const linkQueryValue = getQueryParamDictionary(window.location.href).linkquery;
  if (!!linkQueryValue && linkQueryValue.startsWith(isCMCRequestCreationDeeplinkKey.toLowerCase())){
    return linkQueryValue.substring(isCMCRequestCreationDeeplinkKey.length);
  }
  if (isDeepLinkEnabled && !!linkQueryValue && linkQueryValue.trim() !== '') {
    // Character limit for deep link queries will be same for all experiences.  Take the minimum of available options.
    return linkQueryValue.slice(0, Math.min(SMCExperimentQueryMaxCharacter, QueryMaxCharacter));
  }
  return contactSearchedQuery;
};

export const parseTeamsTheme = (teamsTheme: TeamsTheme) => {
  switch (teamsTheme) {
    case 'dark':
      return 'black';
    case 'contrast':
      return 'highcontrastblack';
    case 'default':
    default:
      return 'colorful';
  }
};

export const isMac = (namespace: string): boolean => {
  if (!namespace) {
    return false;
  }

  return macNamespaces.indexOf(namespace.toLowerCase()) !== -1;
};

export const isWin32 = (namespace: string): boolean => {
  if (!namespace) {
    return false;
  }

  return win32Namespaces.indexOf(namespace.toLowerCase()) !== -1;
};

export const isUWP = (namespace: string): boolean => {
  if (!namespace) {
    return false;
  }

  return uwpNamespaces.indexOf(namespace.toLowerCase()) !== -1;
};

export const makeAADAuthHeader = (token: string) => {
  const bearerFormater = (tokenValue: string) => `Bearer ${tokenValue}`;

  if (!token) {
    return bearerFormater('');
  }

  return token.startsWith('Bearer') ? token : bearerFormater(token);
};

export const makeMSAAuthHeader = (token: string) => {
  const passportFormater = (tokenValue: string) =>
    `Passport1.4 from-PP='${tokenValue}&p='`;

  if (!token) {
    return passportFormater('');
  }

  return token.startsWith('Passport1.4') ? token : passportFormater(token);
};

export const buildAuthToken = (providerType: string, token: string) => {
  switch (providerType) {
    case ClientAuthProvider.AAD:
      return makeAADAuthHeader(token);
    case ClientAuthProvider.MSA:
      return makeMSAAuthHeader(token);
    default:
      return null;
  }
};

export const getThemeFromUrl = () => {
  const queryParamsDictionary = getQueryParamDictionary(window.location.href);
  const theme = !!queryParamsDictionary && !!queryParamsDictionary.theme
    ? queryParamsDictionary.theme.toLowerCase()
    : '';
  if (!!theme && theme === 'dark') {
    return 'darkgray';
  } else {
    return 'white';
  }
};

export const isSignUp = (): boolean => {
  return getSourceAppFromUrl(window.location.href) === SourceApps.SignUp;
};

export const shouldOpenInParentFrame = (): boolean => {
  return isSignUp();
};

export const getPullRightClassName = (isRtl: boolean) => {
  return 'pull-' + (isRtl ? 'left' : 'right');
};

export const getPullLeftClassName = (isRtl: boolean) => {
  return 'pull-' + (isRtl ? 'right' : 'left');
};

export const getAppBrand = (appName: string | null): ContactFormBrand => {
  if (!appName) {
    return ContactFormBrand.Default;
  }

  if (appName.indexOf(ContactFormBrand.GroupMe) > -1) {
    return ContactFormBrand.GroupMe;
  } else if (appName.indexOf(ContactFormBrand.SignUp) > -1) {
    return ContactFormBrand.SignUp;
  }

  return ContactFormBrand.Default;
};

export const isBranded = (appName: string | null): boolean => {
  return getAppBrand(appName) !== ContactFormBrand.Default;
};

/**
 * Adds query params to a URL.
 * @param originalUrl the original URL.
 * @param paramsDictionary a dictionary with query params to add.
 */
export const addQueryParamsToUrl = (
  originalUrl: string,
  paramsDictionary: { [key: string]: string }
): string => {
  const destinationUrl = originalUrl;
  const paramItems: string[] = [];
  Object.keys(paramsDictionary).forEach(
    (k) =>
      paramsDictionary[k] !== undefined &&
      paramsDictionary[k] !== '' &&
      paramItems.push(`${k}=${paramsDictionary[k]}`)
  );
  const paramsStr = paramItems.join('&');
  return `${destinationUrl}${destinationUrl.indexOf('?') > -1 ? '&' : '?'
    }${paramsStr}`;
};

/**
 * Clean query params to a URL.
 * @param paramsDictionary a dictionary with query params to add.
 */
export const cleanQueryParams = (
  paramsDictionary: { [key: string]: string }
): { [key: string]: string } => {
  const cleanParams: { [key: string]: string } = {};
  Object.keys(paramsDictionary).forEach(
    (k) => {
      if (paramsDictionary[k] !== undefined &&
        paramsDictionary[k] !== '') {
          cleanParams[k] = paramsDictionary[k];
      }
    }
  );
  return cleanParams;
};

/**
 * This function is used to add the NS query param to an URL.
 * @param originalUrl The URL to add NS and Version
 * @param ns The namespace to be added
 */
export const addNamespaceToUrl = (
  originalUrl: string,
  ns: string | null
): string => {
  if (ns !== null) {
    return addQueryParamsToUrl(originalUrl, { NS: ns });
  }
  return originalUrl;
};

/**
 * This function is used to add the Ver query param to an URL.
 * @param originalUrl The URL to add NS and Version
 * @param version The version to be added
 */
export const addVersionToUrl = (
  originalUrl: string,
  version: string | null
): string => {
  if (version !== null) {
    return addQueryParamsToUrl(originalUrl, { Ver: version });
  }
  return originalUrl;
};

/**
 * This function is used to add the AppVer query param to an URL.
 * @param originalUrl The URL to add NS and Version
 * @param appVer The AppVer to be added
 * @param sourceApp The SourceApp to be added (currently used for SignUp and IVR case)
 */
export const addAppNamesToUrl = (
  originalUrl: string,
  appVer: string | null,
  sourceApp?: string
): string => {
  const queryParams = !!sourceApp
    ? { SourceApp: sourceApp }
    : !!appVer
      ? { AppVer: appVer }
      : null;
  if (!!queryParams) {
    return addQueryParamsToUrl(originalUrl, queryParams);
  }
  return originalUrl;
};

/**
 * This function is used to add the AppVer query param to an URL.
 * @param queryParams queryParam dictionary
 * @param appVer The AppVer to be added
 * @param sourceApp The SourceApp to be added (currently used for SignUp and IVR case)
 */
 export const addAppNamesToParam = (
  queryParams: {[key: string]: string},
  appVer: string | null,
  sourceApp?: string
): void => {
  if (!!sourceApp) {
    queryParams['SourceApp'] = sourceApp;
  } else if (!!appVer) {
    queryParams['AppVer'] = appVer;
  }
};

/**
 * This function is used to add NS and Version query params to a URL.
 * @param originalUrl The URL to add NS and Version
 * @param ns The namespace to be added
 * @param version The version to be added
 */
export const addNamespaceAndVersionToUrl = (
  originalUrl: string,
  ns: string | null,
  version: string | null
): string => {
  if (ns !== null && version !== null) {
    return addVersionToUrl(addNamespaceToUrl(originalUrl, ns), version);
  }
  return originalUrl;
};

/**
 * This function adds the 'expguid' query param to a URL if the query param is present in the window.location.href
 * That way, experimentation overrides can be added to subsequent API calls.
 * @param originalUrl the original URL to have the experimentation flight override added to it
 */
export const addExpGuidToUrl = (originalUrl: string): string => {
  const queryParams = getQueryParamDictionary(window.location.href);
  if (!queryParams.expguid) {
    return originalUrl;
  }
  return addQueryParamsToUrl(originalUrl, { expguid: queryParams.expguid });
};

/**
 * This function adds the 'expguid' query param to a URL if the query param is present in the window.location.href
 * That way, experimentation overrides can be added to subsequent API calls.
 * @param params the original URL to have the experimentation flight override added to it
 */
export const addExpGuidToParam = (params: {[key: string]: string}): void => {
  const queryParams = getQueryParamDictionary(window.location.href);
  if (!!queryParams.expguid) {
    params['expguid'] = queryParams.expguid;
  }
};

/**
 * Check if the bundled is loaded from Get Help's hosting page by checking the window.emeraldContext.sourceApp value
 * @returns true/false
 */
export const isGetHelp = (): boolean => isGetHelpSourceApp(window.emeraldContext ? window.emeraldContext.sourceApp : '');

/**
 * If window.location.href has query 'usealchemyppe=true':
 * - Add 'usealchemyppe=true' to given params dictionary.
 *
 * If window.location.href has query 'usealchemyppe=false':
 * - Add 'usealchemyppe=false' to given params dictionary.
 *
 * If window.location.href has no query 'usealchemyppe' or 'usealchemyppe' value is not true/false:
 * - For Get Help: add 'usealchemyppe=true/false' to given params dictionary based on Get Help's environment (PROD/PPE/DEV)
 * - For other entry points: do nothing
 *
 * @param params the params dictionary of the axios config
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const addAlchemyPpeFlagToParam = (params: { [key: string]: any }): void => {
  const queryParams = getQueryParamDictionary(window.location.href);

  if (queryParams?.usealchemyppe?.toLowerCase() === 'true') {
    params['useAlchemyPPE'] = true;
    return;
  }

  if (queryParams?.usealchemyppe?.toLowerCase() === 'false') {
    params['useAlchemyPPE'] = false;
    return;
  }

  if (isGetHelp()) { // found key but no value or no key found
    params['useAlchemyPPE'] = appContext.environment.baseUrl !== GetHelpBaseUrl.Prod;
  }
};

/**
 * This function is used to add the market to a URL path.
 * @param originalUrl The URL to add market
 * @param appVer The market to be added
 */
export const addMarketToUrl = (
  originalUrl: string,
  market?: string | null
): string => {
  if (!!market) {
    let destinationUrl = originalUrl;
    if (originalUrl[0] !== '/') {
      destinationUrl = `/${destinationUrl}`;
    }
    return `/${market}${destinationUrl}`;
  }
  return originalUrl;
};

export const isAlchemyEnabledForEnvironmet = (
  environment: Environment,
  enabledEnvironmetList: Array<Environment>
): boolean => {
  let enabled: boolean = false;
  if (!!enabledEnvironmetList && environment != null) {
    enabledEnvironmetList.forEach((env: Environment) => {
      if (env === environment) {
        enabled = true;
        return;
      }
    });
  }

  return enabled;
};

export const isAlchemyEnabledForEnvironmetAndAppVer = (
  environment: Environment,
  appVer: string,
  enabledEnvironmetList: Array<Environment>,
  enabledAppVerList: Array<string>
): boolean => {
  let enabled: boolean = isAlchemyEnabledForEnvironmet(
    environment,
    enabledEnvironmetList
  );
  if (
    enabled &&
    (environment === Environment.agavePane ||
      environment === Environment.nonAgavePane)
  ) {
    enabled = false;
    if (!!enabledAppVerList && !!appVer) {
      enabledAppVerList.forEach((enabledAppVer: string) => {
        if (enabledAppVer === appVer) {
          enabled = true;
          return;
        }
      });
    }
  }

  return enabled;
};

export function FormatString(str: string, ...val: string[]) {
  for (let index = 0; index < val.length; index++) {
    str = str.replace(`{${index}}`, val[index]);
  }
  return str;
}

export function getAutoSuggestWorkloadIdAppName(env: number, sourceApp: string, ns: string) {
  // for surfaceapp, temporarily using smc for auto-suggest;
  // if needed, can be change into getHelp auto-suggest in the future
  if (sourceApp === SourceApps.SMCExp ||
    sourceApp === SourceApps.SMCIVR ||
    sourceApp === SourceApps.SurfaceIVR ||
    sourceApp === SourceApps.SOC2 ||
    sourceApp === SourceApps.SMC2 ||
    sourceApp === SourceApps.SMCIVR2 ||
    sourceApp === SourceApps.SurfaceIVR2 ||
    sourceApp === SourceApps.LightHouse) {
    return { workloadId: '23', appName: 'smc' };
  } else if (sourceApp === SourceApps.SurfaceApp || sourceApp === SourceApps.SurfaceApp2 || sourceApp === SourceApps.SurfaceWeb) {
    return { workloadId: '19', appName: 'gethelp' };
  } else if (sourceApp === SourceApps.SignUp) {
    return { workloadId: '43', appName: 'vsbbusinessassist' };
  } else if (Environment.SOC === env) {
    return { workloadId: '22', appName: 'soc' };
  } else if (Environment.ThirdParty === env) {
    return { workloadId: '1', appName: 'o365commercial' };
  } else if (Namespaces.WinWord === ns || (Environment.agavePane === env && Namespaces.WordWac === ns)) {
    return { workloadId: '4', appName: 'word' };
  } else if (Namespaces.Excel === ns || (Environment.agavePane === env && Namespaces.ExcelWac === ns)) {
    return { workloadId: '20', appName: 'excel' };
  } else if (Namespaces.Powerpoint === ns || (Environment.agavePane === env && Namespaces.PowerPointWac === ns)) {
    return { workloadId: '18', appName: 'powerpoint' };
  } else if (isGetHelpSourceApp(sourceApp)) {
    return { workloadId: '23', appName: 'gethelp' };
  }
  return '';
}

export function getAutoSuggestQueryParams(env: Environment, sourceApp: string, ns: string) {
  const queryParams = AutoSuggestConstants.queryParams;
  const locale: string = getSocMarket();
  const language: string = locale.split('-')[0]; // get base language code
  const workloadId: string = getAutoSuggestWorkloadIdAppName(env, sourceApp, ns)['workloadId'];
  queryParams.$filter = FormatString(
    AutoSuggestConstants.filterClause,
    locale,
    language,
    workloadId
  );
  return queryParams;
}

export function getAutoSuggestQueryParamsV2() {
  const queryParams = AutoSuggestV2Constants.queryParams;
  const locale: string = getSocMarket();
  queryParams.locale = locale;
  return queryParams;
}

export function buildAutoSuggestContactUsFields(
  isDeepLinkQuery: boolean,
  isAnyAutoSuggestEnabled: boolean,
  originalQuery: string,
  suggestions: string[] | string[] | AutoSuggestResult[],
  isAutoSuggestQuery: boolean,
  queryIndex: number,
  alchemyThemeRoutingEnabled: boolean
) {
  return JSON.stringify({
    'isDeepLinkQuery': isDeepLinkQuery,
    'autoSuggestFields': isAnyAutoSuggestEnabled ? {
      'originalQueryLength': originalQuery?.length,
      'suggestionsCount': suggestions?.length,
      'isAutoSuggestQuery': isAutoSuggestQuery,
      'isSuggestionsAvailable': suggestions?.length > 0,
      'queryIndex': queryIndex,
    } : null,
    'alchemyThemeRoutingEnabled': alchemyThemeRoutingEnabled,
  });
}

export const wireupLinksForLogging = (
  anchor: HTMLAnchorElement,
  logAttributes: { [key: string]: { [key: string]: LoggingHtmlAttribute } },
  index: number
): HTMLAnchorElement => {
  if (anchor && anchor.hasAttribute(LinkForLoggingAttribute)) {
    const logName: string = anchor.getAttribute(LinkForLoggingAttribute) || '';
    const attributes = logName ? { ...logAttributes.base, ...logAttributes[logName.toLowerCase()] } : logAttributes.base;
    Object.keys(attributes).forEach((key: string) => {
      const attribute = attributes[key];
      if (attribute) {
        const value = !!attribute.Indexed
          ? `${attribute.Value}-${index}`
          : attribute.Value;
        anchor.setAttribute(attribute.Name, value);
      }
    });
  }
  return anchor;
};

export const wireupLinks = (
  htmlString: string,
  logAttributes: { [key: string]: { [key: string]: LoggingHtmlAttribute } },
  nodeId?: string,
  dialogId?: string,
  solutionType?: string,
  solutionId?: string
): string => {
  let innerHTML = '';
  let errorMessage = '';
  try {
    if (htmlString) {
      const elm = new DOMParser().parseFromString(htmlString, 'text/html');
      if (!!elm) {
        const anchors: HTMLCollection = elm.getElementsByTagName('a');
        const _count = anchors.length;
        let anchor: HTMLAnchorElement;
        for (let i = 0; i < _count; i++) {
          anchor = anchors.item(i) as HTMLAnchorElement;
          anchor = wireupLinksForLogging(anchor, logAttributes, i);
        }
        if (anchor && !anchor.hasAttribute('target')) {
          anchor.setAttribute('target', '_blank');
        }

        if (elm && elm.body) {
          innerHTML = elm.body.innerHTML;
        } else {
          const firstChild = !!elm.firstElementChild
            ? elm.firstElementChild
            : (elm.firstChild as HTMLElement);
          innerHTML = firstChild!.innerHTML;
        }

        // we've discovered that some dialogs have content that needs to be cleaned up,
        // returning html content as is if validation fails
        if (isValidHTML(innerHTML)) {
          return innerHTML;
        } else {
          errorMessage = 'validation failed';
        }
      }
    }
  } catch (e) {
    if (e) {
      errorMessage = 'wireupLinks failed: ' + e.message;
      console.log('wireupLinks failed: ' + e);
    }
  }
  if (!!errorMessage) {
    const message = {
      originalHtml: encodeURIComponent(htmlString),
      parsedHtml: encodeURIComponent(innerHTML),
      nodeId,
      dialogId,
      solutionType,
      solutionId,
      errorMessage,
    };
    const logHandlerTags = {
      content: {
        contentId: 'wireupLinksError',
        contentName: 'wireupLinksError',
        formnm: 'maxFunnel',
        areaName: 'selfHelpAlchemyBranching',
        functionName: 'wireupLinks',
        href: window.location.href,
        message: JSON.stringify(message),
      },
      behavior: 'OTHER',
    };
    captureContentUpdate(logHandlerTags);
  }
  return htmlString;
};

export const getAlchemyAppNameBySourceApp = (sourceApp: SourceApps): string => {
  // don't set alchemyApp to null or undefined as it will be serialized to string "null"/"undefined"
  let alchemyAppName = '';
  // For Admin Teams App, support workflow should be pointed at the Office 365 Commercial alchemy instance
  if (sourceApp === SourceApps.TeamsAdmin || sourceApp === SourceApps.TeamsAdmin2) {
    alchemyAppName = AlchemyApp.TeamsAdminApp;
  } else if (sourceApp === SourceApps.OfficeDotCom || sourceApp === SourceApps.OutlookAdmin) {
    alchemyAppName = AlchemyApp.SupportCentral;
  }
  return alchemyAppName;
};

export const getSourceAppEnum = (sourceapp: string) => {
  const enumKey: string = Object.keys(SourceApps).find((s: string) => s.toLowerCase() === sourceapp.toLowerCase());
  return enumKey && SourceApps[enumKey] ? SourceApps[enumKey] : null;
};

export const getAlchemyClientContext = (getState: () => IAppState) => {
  const windowContextObj = window && window.ClientContext ? window.ClientContext : null;
  const appState = getState();
  const isGetHelp = appState.GlobalState.environment === Environment.GetHelp;
  if (isGetHelp) {
    const contextObject: any = {
      DiagnosticCapabilities: [],
      Flighting: [],
      Licenses: [],
      SystemInfo: { OS: appContext.systemInfo?.OS ?? '', MajorOSVersion: appContext.systemInfo?.MajorOSVersion ?? -1 },
    };

    if (!!appContext.diagcap && appContext.diagcap.length > 0) {
      const list: string[] = appContext.diagcap.split('+');
      if (!!list && list.length > 0) {
        contextObject.DiagnosticCapabilities = list.filter((str: string) => str in DiagnosticCapabilityType);
      }
    }
    const licenses = appState.GlobalState.licenses;
    const ecsState = appState.GlobalState.getHelpECS;

    const mergedFlightList = (appContext.featureFlightList ?? []).concat(ecsState.flightList ?? []);

    contextObject.Flighting = mergedFlightList;
    contextObject.Licenses = licenses;

    return contextObject;
  }
  return windowContextObj;
};

export const getAlchemySolutionsUrlAndPayload = (getState: () => IAppState, callType: AlchemySolutionsCallType, query: string, sourceApp: SourceApps, tenantInfo: TenantInfo, alchemyAppName: string = '', relatedSHCorrelationIds: string = '', isSpecialQuery: boolean = false, bypassPreProcess: boolean = false): AlchemySolutionsCall => {
  const appState = getState();
  const isGetHelp = appState.GlobalState.environment === Environment.GetHelp;
  const url = URLs.AlchemySolutionsUrlV1;
  const dataBoundary = getDataBoundaryHeaderValue(tenantInfo);
  const requestHeaders = { ...JsonHeaders, 'X-Ms-DataBoundary': dataBoundary };
  const payload: AlchemySolutionsPayload = {
    Text: query,
  };

  if (window && window.ClientContext) {
    payload.ClientContext = { ...window.ClientContext};

    if (isSignUp()) {
      const cartSid = new URL(window.location.href).searchParams.get("cartSid");
      payload.ClientContext.CartSid = cartSid;
    }
  }

  switch (callType) {
    case AlchemySolutionsCallType.AlchemySolutions:
      payload.RelatedSHCorrelationIds = relatedSHCorrelationIds;
      const overrideAppName = getAlchemyAppNameBySourceApp(sourceApp);
      if (!!overrideAppName) {
        payload.App = overrideAppName;
      }
      if (isGetHelp) {
        requestHeaders['X-CorrelationId'] = appContext.sessionId;
      }
      break;
    case AlchemySolutionsCallType.AlchemyProactive:
    case AlchemySolutionsCallType.AlchemyFetchNotifications:
      payload.App = AlchemyApp.GetHelpRecommendation;
      break;
    case AlchemySolutionsCallType.AlchemyUnAuthBranching:
      payload.App = alchemyAppName;
      break;
  }

  if (isGetHelp) {
    payload.IsSpecialQuery = isSpecialQuery;
    payload.BypassSpellcheck = bypassPreProcess || !!isSpecialQuery;
    payload.BypassTextTranslation = bypassPreProcess || isSpecialQuery;
    payload.UserType = appState.GlobalState.environment === Environment.GetHelp ? appContext.emeraldUserType : null;
    payload.ClientContext = getAlchemyClientContext(getState);
    payload.Locale = appState.GlobalState.market;
  }

  return { Url: url, RequestHeaders: requestHeaders, Payload: payload };
};

/**
 * Perform shallow equality checks
 */
export const shallowEquals = (val1: any, val2: any): boolean => {
  return val1 == val2;
};

/** check browser type */
export const getBrowserType = (): string => {
  const { userAgent } = navigator;

  if (!userAgent) {
    return '';
  }

  let match = userAgent.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];
  let temp;

  if (/trident/i.test(match[1])) {
    temp = /\brv[ :]+(\d+)/g.exec(userAgent) || [];
    return `IE ${temp[1] || ''}`;
  }

  if (match[1] === 'Chrome') {
    temp = userAgent.match(/\b(OPR|Edge)\/(\d+)/);

    if (temp !== null) {
      return temp.slice(1).join(' ').replace('OPR', 'Opera');
    }

    temp = userAgent.match(/\b(Edg)\/(\d+)/);

    if (temp !== null) {
      return temp.slice(1).join(' ').replace('Edg', 'Edge (Chromium)');
    }
  }

  match = match[2] ? [match[1], match[2]] : [navigator.appName, navigator.appVersion, '-?'];
  temp = userAgent.match(/version\/(\d+)/i);

  if (temp !== null) {
    match.splice(1, 1, temp[1]);
  }

  return match.join(' ');
};

export const getFileMimeType = (fileName: string | null, mimeType: string): string => {
  if (!mimeType && !!fileName) {
    const end = fileName.length;
    const start = fileName.length - 4;
    if (fileName.substring(start, end) === AttachmentsConstants.MsgExtension) {
      return AttachmentsConstants.MsgMimeType;
    }
    return AttachmentsConstants.FallbackMimeType;
  }
  return mimeType;
};
// Check to see if the agent modality is out of business hours(OOBH).
export const isAgentModalityOOBH = (agentModality: WaitTimeResponseModel): boolean => {
  return !!agentModality &&
    ((agentModality.Reason == WaitTimeResponseReason.OutOfBusinessHours ||
      agentModality.Reason == WaitTimeResponseReason.SpecialistOutOfBusinessHours) &&
      !!agentModality.Info &&
      !!agentModality.Info.BusinessHours &&
      agentModality.Info.BusinessHours.length > 0);
};

// Return agent modalities in business hours(InBH).
export const getAgentModalitiesInBH = (modalities: WaitTimeResponseModel[]) => {
  return !!modalities ? modalities.filter(agentModality => !isAgentModalityOOBH(agentModality)) : [];
};

// Return agent modalities outside of business hours(OOBH).
export const getAgentModalitiesOOBH = (modalities: WaitTimeResponseModel[]) => {
  return !!modalities ? modalities.filter(agentModality => isAgentModalityOOBH(agentModality)) : [];
};

// Indicates if the request is PayPerIncident/AgentAssist scenario.  If it is,then return the metadata key used.
export const getUpsellMetaDataKey = (freelanceRequest: FreelanceRequest): string => {
  let upsellMetaDataKey = null;
  if (!!freelanceRequest && !!freelanceRequest.MetaDatas && freelanceRequest.MetaDatas.length > 0) {
    freelanceRequest.MetaDatas.forEach((metaDataKeyValue: RaveMetaData) => {
      if ((metaDataKeyValue.Key === MetaDataKeys.IsAgentAssistSubscriptionSignup || metaDataKeyValue.Key === MetaDataKeys.IsPayPerIncidentExperiment) &&
        metaDataKeyValue.Value === 'true') {
        upsellMetaDataKey = metaDataKeyValue.Key;
      }
    });
  }
  return upsellMetaDataKey;
};

// Returns upsell link to display on RequestConfirmation screen for AgentAssistSubscription scenario
export const getUpsellAgentAssistSubscriptionLink = (modalityResponse: ModalityResponseModel): string => {
  let upsellAgentAssistSubscriptionLink = null;
  if (!!modalityResponse &&
    !!modalityResponse.NonAgentModalities &&
    !!modalityResponse.NonAgentModalities.Modalities &&
    modalityResponse.NonAgentModalities.Modalities.length > 0
  ) {
    const agentAssistNonAgentModality = modalityResponse.NonAgentModalities.Modalities.filter((nonAgentModality: NonAgentModality) =>
      nonAgentModality.Id.toLowerCase().indexOf(M365UpsellNonAgentModalityId.agentAssistSubscription) !== -1);
    if (!!agentAssistNonAgentModality && agentAssistNonAgentModality.length > 0) {
      upsellAgentAssistSubscriptionLink = agentAssistNonAgentModality[0].Link;
    }
  }
  return upsellAgentAssistSubscriptionLink;
};

// Checks if PayPerIncident/AgentAssist/ChatWeighted is returned from GetAvailableModality call
export const getUpsellModalityId = (modalityResponse: ModalityResponseModel): string => {
  let upsellModalityId = '';
  if (!!modalityResponse &&
    !!modalityResponse.NonAgentModalities &&
    !!modalityResponse.NonAgentModalities.Modalities &&
    modalityResponse.NonAgentModalities.Modalities.length > 0
  ) {
    modalityResponse.NonAgentModalities.Modalities.some((nonAgentModality: NonAgentModality) => {
      const nonAgentModalityId = nonAgentModality.Id.toLowerCase();
      if (nonAgentModalityId.indexOf(M365UpsellNonAgentModalityId.agentAssistSubscription) !== -1 || nonAgentModalityId.indexOf(M365UpsellNonAgentModalityId.payPerIncident) !== -1) {
        upsellModalityId = nonAgentModalityId;
        return true;
      } else {
        return false;
      }
    });
  }
  return upsellModalityId;
};

export const sanitizeHTML = (text: string, allowedAttrs?: string[], allowedURIRegExp?: RegExp) => {
  const sanitizer = dompurify.sanitize;
  return { __html: sanitizer(text, { ADD_ATTR: allowedAttrs, ALLOWED_URI_REGEXP: allowedURIRegExp }) };
};

export const IsInAgeBracket = (userInfo: UserInfo, ageBracket: AgeBracket, isSignedIn: boolean) => {
  if (!isSignedIn) {
    return ageBracket === AgeBracket.Adult;
  }

  switch (userInfo.LegalAgeGroup) {
    case 1:
    case 2:
      return ageBracket === AgeBracket.Child;
    case 4:
      return ageBracket === AgeBracket.Minor;
    default:
      return ageBracket === AgeBracket.Adult;
  }
};

// get request headers for all Rave/DfM related apis
export const getContactSupportRequestHeaders = (getState: () => IAppState): Record<string, any> => {
  const appState = getState();
  const isGetHelp = isGetHelpSourceApp(appState.GlobalState.sourceApp);
  const authInfo = appState.ContactState.ModalitySectionState.authInfo;
  const isDfM = !!appState.GlobalState.tenantInfo && !!appState.GlobalState.tenantInfo.isDfM;
  const dataBoundary = getDataBoundaryHeaderValue(appState.GlobalState.tenantInfo);
  const headers: Record<string, any> = {
    ...JsonHeaders,
    'X-Ms-DataBoundary': dataBoundary,
    'X-Ms-IsDfM': isDfM,
  };

  if (
    (isGetHelp ||
      appState.GlobalState.sourceApp === SourceApps.SurfaceApp ||
      appState.GlobalState.sourceApp === SourceApps.SurfaceApp2 ||
      appState.GlobalState.sourceApp === SourceApps.OneDriveWeb ||
      appState.GlobalState.sourceApp === SourceApps.Teams ||
      appState.GlobalState.sourceApp === SourceApps.Teams2 ||
      appState.GlobalState.sourceApp === SourceApps.TeamsAdmin ||
      appState.GlobalState.sourceApp === SourceApps.TeamsAdmin2 ||
      appState.GlobalState.sourceApp === SourceApps.OfficeDotCom ||
      appState.GlobalState.sourceApp === SourceApps.OutlookAdmin) &&
    !!authInfo
  ) {
    headers['Authorization'] = buildAuthToken(authInfo.provider, authInfo.token);
    headers['X-CorrelationId'] = getRandomGuidString();
  }

  return headers;
};

// get data boundary header
export const getDataBoundaryHeaderValue = (tenantInfo: TenantInfo) => {
  let dataBoundary: string[] = [];
  if (tenantInfo &&
    tenantInfo.dataBoundary &&
    tenantInfo.dataBoundary.length > 0) {
    dataBoundary = tenantInfo.dataBoundary;
  }
  return dataBoundary;
};

export const constructUserPhone = (request: FreelanceRequest, dialingCode?: string): string => {
  let userPhone = '';
  if (!!request) {
    dialingCode = dialingCode || request.UserCountryDialingCode;
    // we allow creating ticket with email or phone and we don't have a requirement to provide both, so we'll add this null check to set phone
    if (!!request.UserPhoneWithoutDialingCode) {
      userPhone = dialingCode ? `${dialingCode}-${request.UserPhoneWithoutDialingCode}` : request.UserPhoneWithoutDialingCode;
    }

    if (!!userPhone) {
      // Remove any non-digit chars, '-' will break c2c
      userPhone = userPhone.replace(/[^0-9]/g, '');
    }
  }
  return userPhone;
};

export const getEmailFromCustomerContacts = (
  request: Partial<FreelanceRequest>
): string => {
  if (!request) {
    return '';
  }
  let userEmail = request.UserEmail;
  const customerContacts = request.CustomerContacts;
  if (customerContacts && customerContacts.length > 0) {
    let customerContactEmail = '';
    customerContacts.forEach((c: CustomerContact) => {
      if (c.Email) {
        customerContactEmail = !!customerContactEmail
          ? `${customerContactEmail};${c.Email}`
          : c.Email;
      }
    });
    userEmail = customerContactEmail || userEmail;
  }
  return userEmail;
};

export const updateDraftRequestMetadata = (getState: () => IAppState) => {
  const appState = getState();
  let draftRequest = appState.ContactState.ModalitySectionState.draftRequest;
  const isGetHelp = isGetHelpSourceApp(appState.GlobalState.sourceApp);
  if (isGetHelp) {
    draftRequest = deepCopyProperty(draftRequest);
    const metaDatas = !!draftRequest.MetaDatas ? [...draftRequest.MetaDatas] : [];
    const configurations = appState.GlobalState.getHelpConfig;
    // Collect System Info
    if (configurations && configurations.SystemInfo) {
      for (const [key, val] of Object.entries(configurations.SystemInfo)) {
        // Rave Metadata expects uppercase Key and Value keys
        // Todo: Brook - verify what needs to be done if there are duplicate keys
        metaDatas.push({ Key: key + '(system_info)', Value: val.toString() });
      }
    }
    if (appState.ContactState.SharedState.isWindowsCopilotRequestCreationDeeplink){
      metaDatas.push({Key: 'WindowsCopilotReferrerDeepLink', Value: window.location.href})
    }
    // check to see if duplicated keys have been added
    const duplicatedKeys: string[] = [];
    const metaKeys: string[]= [];
    metaDatas.forEach((metdata: RaveMetaData) => {
      if (metdata) {
        if (metaKeys.includes(metdata.Key)) {
          duplicatedKeys.push(metdata.Key);
        }
        metaKeys.push(metdata.Key);
      }
    });

    if (duplicatedKeys.length > 0) {
      const logHandlerTags = {
        content: {
          contentId: 'requestMetadataDuplicatedKeys',
          contentName: 'requestMetadataDuplicatedKeys',
          formnm: 'maxFunnel',
          areaName: 'modalitiesSection',
          functionName: 'updateDraftRequestMetadata',
          href: window.location.href,
          message: encodeURIComponent(JSON.stringify(duplicatedKeys)),
        },
        behavior: 'CONTACT',
      };
      captureContentUpdate(logHandlerTags);
    }
    draftRequest.MetaDatas = metaDatas;
  }
  return draftRequest;
};

export const updateCustomerContacts = (
  request: FreelanceRequest
): void => {
  const customerContacts: Array<CustomerContact> = [];
  if (!!request) {
    const userCountryDialingCode: string = request.UserCountryDialingCode;
    const userPhoneWithoutDialingCode: string =
      request.UserPhoneWithoutDialingCode;
    const userEmail: string = request.UserEmail;
    const emails = userEmail ? userEmail.split(';') : [];
    if (emails.length > 0) {
      for (let idx = 0; idx < emails.length; idx++) {
        const emailId = emails[idx].trim();
        if (emailId.length === 0) {
          continue;
        }
        customerContacts.push({
          // For first customerContact, PrimaryContact = true, PhoneNumber = userPhoneWithoutDialingCode.
          // For others: PrimaryContact = false, PhoneNumber = null
          PrimaryContact: customerContacts.length === 0,
          Email: emailId,
          PhoneNumber:
            customerContacts.length === 0 ? userPhoneWithoutDialingCode : null,
          // we allow phone or email, so phone might be empty and we don't send dialing code in that case
          DialingCode:
            customerContacts.length === 0 && !!userPhoneWithoutDialingCode
              ? userCountryDialingCode
              : null,
        } as CustomerContact);
      }
    }
    // email is null
    else if (!!userPhoneWithoutDialingCode) {
      customerContacts.push({
        // For first customerContact, PrimaryContact = true, PhoneNumber = userPhoneWithoutDialingCode.
        // For others: PrimaryContact = false, PhoneNumber = null
        PrimaryContact: true,
        Email: '',
        PhoneNumber: userPhoneWithoutDialingCode,
        DialingCode: userCountryDialingCode,
      } as CustomerContact);
    }
    request.CustomerContacts = customerContacts;
  }
};

export const getBuildVersionString = (): string => {
  const buildVersionStr = getMetaTagAttributeValue(AwaMetaTagType.BuildVersion, ContentAttribute);
  if (!!buildVersionStr) {
    const buildVersionRegEx = /(\d{2})(\d{1})(\d{5})(\d{5})/gi;
    return buildVersionStr.replace(buildVersionRegEx, '$1.$2.$3.$4');
  }
  return '';
};

export const deepCopyProperty = (property: any): any => {
  if (!property) { // Take care of undefined and null values
    return property;
  }
  let deepcopy = property;
  try {
    deepcopy = JSON.parse(JSON.stringify(property));
  } catch (e) {
    if (e) {
      const logHandlerTags = {
        content: {
          contentId: 'JsonParseDeepCopyError',
          contentName: 'JsonParseDeepCopyError',
          formnm: 'maxFunnel',
          href: window.location.href,
          errorMessage: e.message,
        },
        behavior: 'OTHER',
      };
      captureContentUpdate(logHandlerTags);
    }
  }
  return deepcopy;
};

export const isDfMRequest = (requestId: string): boolean => {
  const guidRegEx = new RegExp('^[{]?[0-9a-f]{8}-([0-9a-f]{4}-){3}[0-9a-f]{12}[}]?$', 'i');
  return !!requestId && !guidRegEx.test(requestId);
};

export const onOverrideClickBehavior = (articleOnClickHandler: (href: string) => void) => (event?: React.MouseEvent) => {
  if (!!event && !!event.target) {
    const target = event.target as HTMLElement;
    const href = target.hasAttribute('href') ? target.getAttribute('href') : null;
    if (isArticleSupportedInApp(href) && articleOnClickHandler) {
      event.preventDefault();
      event.stopPropagation();
      articleOnClickHandler(href);
    }
  }
};

export const getRequestLocale = (request: FreelanceRequest): string => {
  var locale: string = '';
  var language: string = '';
  var country: string = '';
  if (!!request && !!request.MetaDatas) {
    for (const metaData of request?.MetaDatas) {
      if (metaData?.Key === MetaDataKeys.Language) {
        language = metaData.Value;
      }
      if (metaData?.Key === MetaDataKeys.Country) {
        country = metaData.Value;
      }
      if (!!language && !!country) {
        locale = `${language}-${country}`;
        break;
      }
    }
  }

  return locale;
};

export const isRequestCreationDeepLink = (url: string, copilotKey: string): boolean => {
  return url.toLowerCase().indexOf(copilotKey.toLowerCase()) != -1;
}

export const getQueryType = (isDeeplink: boolean, isAutoSuggest: boolean): QueryType => {
  if (isDeeplink) {
    return QueryType.Deeplink;
  } else if (isAutoSuggest) {
    return QueryType.AutoSuggest;
  }
  return QueryType.UserTyped;
};

// Gets the app invoker from the URI. The invoker is the part of the URI that comes after the stopAt string.
// For example, if the URI is "ms-contact-support://settings//?Product=Windows&SearchKey=Fix%20Bluetooth%20problems%20in%20Windows"
// The app invoker would be "settings".
export const getAppInvoker = (uri: string, stopAt: string = "//") : string => {
  if (uri && uri.trim()) {
    let charLocation = uri.indexOf(stopAt);

    if (charLocation > 0) {
      let preInvoker = uri.substring(charLocation + 2);
      let charLocation2 = preInvoker.indexOf('/');

      if (charLocation2 > 0) {
        return preInvoker.substring(0, charLocation2);
      }
      return preInvoker.replace(/%20/g, ' ').replace('/?', '');
    }
  }
  return '';
}